import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PullToRefresh, InfiniteScroll, Toast, Image } from 'antd-mobile';
import { getCenterList } from './api';
import { getAliyunImg } from 'src/utils/utils';
import './style/centerListStyle.scss';

export default function CenterList({ activeTab, searchKey, sortType, plateTypes = [], provinces = [], organizations = [], scrollMode = 'self' }) {
    const navigate = useNavigate();
    const [dataList, setDataList] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef(null);

    // 加载列表数据
    const loadData = useCallback(async (isRefresh = false) => {
        if (loading) return;

        // 取消之前的请求
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);
        const currentPage = isRefresh ? 1 : pageIndex;

        try {
            const params = {
                type: activeTab === 'supply' ? '供应' : '需求',
                pageIndex: currentPage,
                pageSize: 10,
                plateTypes: plateTypes,
                provinces: provinces,
                organizations: organizations
            };

            // 添加搜索条件
            if (searchKey) {
                params.cName = searchKey;
            }

            // 添加排序
            if (sortType) {
                params.sort = sortType;
            }

            const response = await getCenterList(params);

            if (response && response.ok) {
                const newList = response.data.Items || [];

                if (isRefresh) {
                    setDataList(newList);
                    setPageIndex(2);
                } else {
                    setDataList(prevList => [...prevList, ...newList]);
                    setPageIndex(currentPage + 1);
                }

                setHasMore(response.data.HasNext);
            }
        } catch (error) {
            // 忽略被取消的请求错误
            if (error.name === 'AbortError' || error.message?.includes('abort')) {
                return;
            }
            console.error('加载列表失败:', error);
            Toast.show({
                icon: 'fail',
                content: '加载失败，请重试'
            });
        } finally {
            setLoading(false);
        }
    }, [activeTab, searchKey, sortType, plateTypes, provinces, organizations, pageIndex, loading]);

    // 监听 tab 切换、搜索、排序、筛选条件变化
    useEffect(() => {
        loadData(true);

        // 组件卸载时取消请求
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [activeTab, searchKey, sortType, JSON.stringify(plateTypes), JSON.stringify(provinces), JSON.stringify(organizations)]);

    // 下拉刷新
    const onRefresh = useCallback(async () => {
        await loadData(true);
    }, [loadData]);

    // 上拉加载更多
    const loadMore = useCallback(async () => {
        await loadData(false);
    }, [loadData]);

    // 获取板块徽章
    const getCategoryBadge = (plateTypeName) => {
        const badges = {
            '生猪交易': { text: '生猪', className: 'badge-pig' },
            '设备交易': { text: '设备', className: 'badge-equipment' },
            '猪场租赁': { text: '租赁', className: 'badge-rental' },
            '运输车辆': { text: '运输', className: 'badge-car' }
        };
        return badges[plateTypeName] || { text: plateTypeName, className: 'badge-pig' };
    };

    // 构建详情数据
    const buildDetailData = (item) => {
        const detailData = item.type === '供应' ?
            (item.supplyPig || item.supplyEquipment || item.supplyFarm || item.supplyCar) :
            (item.needPig || item.needEquipment || item.needFarm || item.needCar);

        return detailData;
    };

    // 渲染供应列表项
    const renderSupplyItem = (item) => {
        const badge = getCategoryBadge(item.plateTypeName);
        const detailData = buildDetailData(item);

        if (!detailData) return null;

        // 获取封面图片（从attachments中找第一张fileType为image的图片）
        const getCoverImage = () => {
            if (detailData.attachments && detailData.attachments.length > 0) {
                const firstImage = detailData.attachments.find(att => att.fileType === 'image');
                if (firstImage) {
                    return getAliyunImg(firstImage.filePath);
                }
            }
            return null;
        };

        const coverImage = getCoverImage();

        // 构建属性标签
        const tags = [];
        if (item.supplyPig) {
            tags.push(item.supplyPig.varietyName);
            tags.push(item.supplyPig.growthStageName);
            tags.push(`${item.supplyPig.minWeight}-${item.supplyPig.maxWeight}斤`);
        } else if (item.supplyEquipment) {
            if (item.supplyEquipment.equipmentTypeName) tags.push(item.supplyEquipment.equipmentTypeName);
            if (item.supplyEquipment.degreeName) tags.push(item.supplyEquipment.degreeName);
            if (item.supplyEquipment.brand) tags.push(item.supplyEquipment.brand);
        } else if (item.supplyFarm) {
            if (item.supplyFarm.pigFarmName) tags.push(item.supplyFarm.pigFarmName);
            if (item.supplyFarm.currentName) tags.push(item.supplyFarm.currentName);
            if (item.supplyFarm.designStock) tags.push(`${item.supplyFarm.designStock}头`);
        } else if (item.supplyCar) {
            tags.push(`可装${item.supplyCar.count}头`);
            tags.push(`${item.supplyCar.tonnage}吨`);
            if (item.supplyCar.facilities && item.supplyCar.facilities.length > 0) {
                tags.push(item.supplyCar.facilities.slice(0, 2).map(f => f.name).join('、'));
            }
        }

        return (
            <div
                key={item.id}
                className="list-item"
                onClick={() => navigate(`/detail/${item.id}`)}
            >
                <div className="list-item-img-wrapper">
                    <div className={`category-badge ${badge.className}`}>
                        {badge.text}
                    </div>
                    {coverImage ? (
                        <Image
                            src={coverImage}
                            fit="cover"
                            style={{ width: '100%', height: '100%', borderRadius: '8px' }}
                        />
                    ) : (
                        <div className="list-item-default-img">
                            <svg className="svg-icon" viewBox="0 0 24 24" style={{ width: 40, height: 40, color: '#999' }}>
                                <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM5 17l3.5-4.5 2.5 3.01L14.5 11l4.5 6H5z" fill="currentColor" />
                            </svg>
                        </div>
                    )}
                </div>
                <div className="list-item-content">
                    <div className="list-item-title">
                        {item.state === 50 && (
                            <span className="status-tag status-tag-new">上架中</span>
                        )}
                        {item.title}
                    </div>
                    <div className="attribute-tags">
                        {tags.filter(Boolean).map((tag, idx) => (
                            <span key={idx} className="attribute-tag">{tag}</span>
                        ))}
                    </div>
                    <div className="list-item-meta">
                        <div className="list-item-price">
                            {detailData.minPrice ? `${detailData.minPrice}${detailData.priceUnit ? `${detailData.priceUnit}` : ''}` : '面议'}
                        </div>
                        <div className="list-item-info">
                            {item.browseCount || 0}次浏览
                        </div>
                    </div>
                    <div className="list-item-publisher">
                        {item.supplyCar ? (
                            // 运输车辆显示出发地→目的地
                            `${detailData.province}-${detailData.city}-${detailData.district} → ${detailData.desProvince}-${detailData.desCity}-${detailData.desDistrict}`
                        ) : (
                            // 其他板块显示单个地区
                            `${detailData.province}-${detailData.city}-${detailData.district}`
                        )}
                        · {item.stateUser} {item.timeDec}
                    </div>
                </div>
            </div>
        );
    };

    // 渲染需求列表项
    const renderDemandItem = (item) => {
        const badge = getCategoryBadge(item.plateTypeName);
        const detailData = buildDetailData(item);

        if (!detailData) return null;

        // 构建标签
        const tags = [];
        if (item.needPig) {
            tags.push(item.needPig.varietyName);
            tags.push(`${item.needPig.minWeight}-${item.needPig.maxWeight}斤`);
            tags.push(`${item.needPig.count}头`);
        } else if (item.needEquipment) {
            tags.push(item.needEquipment.equipmentTypeName);
            tags.push(item.needEquipment.gradeName);
            tags.push(`${item.needEquipment.count}套`);
        } else if (item.needFarm) {
            if (item.needFarm.pigFarmName) tags.push(item.needFarm.pigFarmName);
            tags.push(`${item.needFarm.minScale}-${item.needFarm.maxScale}头`);
        } else if (item.needCar) {
            tags.push(`需运${item.needCar.count}头`);
            tags.push(`${item.needCar.tonnage}吨`);
            if (item.needCar.facilities && item.needCar.facilities.length > 0) {
                tags.push(item.needCar.facilities.slice(0, 2).map(f => f.name).join('、'));
            }
        }

        return (
            <div
                key={item.id}
                className="demand-card"
                onClick={() => navigate(`/detail/${item.id}`)}
            >
                <div className="demand-card-header">
                    <div className="demand-card-publisher">
                        <div className="demand-card-avatar personal">
                            <svg className="svg-icon" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor" />
                            </svg>
                        </div>
                        <div>
                            <div className="demand-card-name">{item.user?.nickName}</div>
                        </div>
                    </div>
                    <div className="demand-card-time">{item.timeDec}</div>
                </div>
                <div className="demand-card-title">{item.title}</div>
                <div className="demand-card-tags">
                    <span className={`demand-card-tag category ${badge.className}`}>{badge.text}</span>
                    {tags.filter(Boolean).map((tag, idx) => (
                        <span key={idx} className="demand-card-tag">{tag}</span>
                    ))}
                </div>
                <div className="demand-card-budget">
                    <span className="demand-card-budget-label">预算：</span>
                    {detailData.minPrice ? `¥${detailData.minPrice}${detailData.priceUnit ? `${detailData.priceUnit}` : ''}` : '面议'}
                    {detailData.maxPrice ? `-${detailData.maxPrice}` : ''}
                </div>
                <div className="demand-card-footer">
                    <div className="demand-card-location">
                        <svg viewBox="0 0 24 24">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor" />
                        </svg>
                        {item.needCar ? (
                            // 运输车辆显示出发地→目的地
                            `${detailData.province} ${detailData.city} ${detailData.district} → ${detailData.desProvince} ${detailData.desCity} ${detailData.desDistrict}`
                        ) : (
                            // 其他板块显示单个地区
                            `${detailData.province} ${detailData.city} ${detailData.district}`
                        )}
                    </div>
                    {/* <div className="demand-card-response">
                        {item.consultCount || 0}人响应
                    </div> */}
                </div>
            </div>
        );
    };

    return (
        <div className={`center-list-wrapper ${scrollMode === 'self' ? 'scroll-self' : 'scroll-parent'}`}>
            <PullToRefresh onRefresh={onRefresh}>
                <div className="center-list">
                    {dataList.length === 0 && !loading ? (
                        <div className="empty-state">
                            <svg className="empty-icon" viewBox="0 0 24 24">
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" fill="currentColor" />
                            </svg>
                            <div className="empty-title">暂无数据</div>
                            <div className="empty-desc">
                                {activeTab === 'supply' ? '当前没有供应信息' : '当前没有需求信息'}
                            </div>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'supply'
                                ? dataList.map(item => renderSupplyItem(item))
                                : dataList.map(item => renderDemandItem(item))
                            }
                            <InfiniteScroll loadMore={loadMore} hasMore={hasMore}>
                                {hasMore ? (
                                    <div style={{ padding: '12px 0', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                                        加载中...
                                    </div>
                                ) : dataList.length > 0 ? (
                                    <div style={{ padding: '12px 0', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                                        没有更多了
                                    </div>
                                ) : null}
                            </InfiniteScroll>
                        </>
                    )}
                </div>
            </PullToRefresh>
        </div>
    );
}